'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretLeft,
  faCaretRight,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons';

interface ResultsPaginationProps {
  currentPage: number;
  numberOfPages: number;
  totalCount: number;
  startIndex: number;
  endIndex: number;
  displayCount: number;
  handlePageChange: (page: number) => void;
  isPageDropdownOpen: boolean;
  setIsPageDropdownOpen: (isOpen: boolean) => void;
}

const ResultsPagination: React.FC<ResultsPaginationProps> = ({
  currentPage,
  numberOfPages,
  totalCount,
  startIndex,
  endIndex,
  displayCount,
  handlePageChange,
  isPageDropdownOpen,
  setIsPageDropdownOpen,
}) => {
  if (totalCount === 0 || numberOfPages <= 1) {
    return null;
  }

  return (
    <div className="self-stretch h-14 p-4 border-t border-neutral-200 inline-flex justify-between items-center bg-white">
      <div className="flex justify-center items-center gap-1">
        <div className="text-right justify-start text-secondary/60 text-sm font-normal font-['Inter'] leading-tight">
          {displayCount > 0
            ? `Showing ${startIndex}-${endIndex} of ${totalCount}`
            : 'No results'}
        </div>
      </div>
      <div className="flex justify-center items-center gap-2">
        <button
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          className="w-8 h-8 px-4 py-2 rounded-full flex justify-center items-center gap-2.5 cursor-pointer hover:bg-neutral-50 border border-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <Icon icon={faCaretLeft} className="text-neutral-600 w-4 h-4" />
        </button>
        <div className="relative">
          <button
            onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
            className="h-8 px-3 py-3.5 bg-common-White rounded-full flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-50 border border-neutral-200 -outline-offset-1"
          >
            <div className="justify-start text-text-secondary text-sm font-normal font-['Inter'] leading-tight">
              Page {currentPage}
            </div>
            <Icon icon={faCaretDown} className="text-neutral-600 w-2.5 h-1.5" />
          </button>
          {isPageDropdownOpen && (
            <div className="absolute bottom-full mb-1 left-0 w-auto whitespace-nowrap bg-white rounded-lg shadow-lg border border-neutral-200 py-1 max-h-60 overflow-y-auto z-50">
              {Array.from({ length: numberOfPages }, (_, i) => (
                <div
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className="px-3 py-2 hover:bg-neutral-50 cursor-pointer text-text-secondary text-sm font-normal font-['Inter']"
                >
                  Page {i + 1}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() =>
            currentPage < numberOfPages && handlePageChange(currentPage + 1)
          }
          className="w-8 h-8 px-4 py-2 rounded-full flex justify-center items-center gap-2.5 cursor-pointer hover:bg-neutral-50 border border-neutral-200 -outline-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentPage === numberOfPages}
          aria-label="Next page"
        >
          <Icon icon={faCaretRight} className="text-neutral-600 w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ResultsPagination;
